/**
 * Coalesce rapid callbacks to at most one run per interval.
 *
 * Leading + trailing: the first call runs immediately, later calls within the
 * window collapse to a single trailing run. Used to keep Plotly.react off the
 * live-data hot path without dropping the latest point.
 */
export function createCoalescedScheduler(intervalMs: number): {
  schedule: (fn: () => void) => void;
  cancel: () => void;
} {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let lastRun = 0;
  let pending: (() => void) | null = null;

  const run = (fn: () => void): void => {
    lastRun = Date.now();
    pending = null;
    fn();
  };

  return {
    schedule(fn: () => void): void {
      const elapsed = Date.now() - lastRun;
      if (elapsed >= intervalMs && timer == null) {
        run(fn);
        return;
      }
      pending = fn;
      if (timer == null) {
        timer = setTimeout(() => {
          timer = null;
          if (pending) run(pending);
        }, Math.max(0, intervalMs - elapsed));
      }
    },
    cancel(): void {
      if (timer != null) {
        clearTimeout(timer);
        timer = null;
      }
      pending = null;
    },
  };
}
